import React, { useState } from 'react';
import {
  Box,
  Chip,
  Container,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_ICON_BUTTON, V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import { medicalSupplyMethodKeys } from '../../models/injection';
import { toI18nParams, notifications, extractApiErrorMessage, probeLocalNetwork } from '../../utils';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import Autocomplete from '@mui/material/Autocomplete';
import useAuthContext from '../../hooks/useAuthContext';
import { AutocompleteInput, Loader, Title, TablePaginationWrapper } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { PRINTER_ADAPTER_HUB_URL, PRINTER_ADAPTER_ORIGIN } from '../../constants/printer-adapter';
import { usePrinterAdapterInstaller } from '../../hooks/usePrinterAdapterInstaller';
import LabelPrintTaskService from '../../hooks/LabelPrintTaskService';
import {
  LabelPrintCommand,
  LabelPrintTask,
  LabelPrintTaskStatus,
  LogLineResult,
  PrintCommand
} from '../../models/labels';
import { AnimalType } from '../../models/animal';
import LabelPreviewDialog from './components/LabelPreviewDialog';
import PrintLabelsDialog from './components/PrintLabelsDialog';
import ConnectionStatePopper from './components/ConnectionStatePopper';
import {
  HealthSystemDALModelVetPrinterConfig,
  HealthSystemDomainFarmsResultFarmViewModel,
  HealthSystemDomainVetsResultVeterinaryViewModel
} from '../../api/HealthSystemApi';
import PrintCommandConfigModal from './components/PrintCommandConfigModal';
import { red } from '@mui/material/colors';
import ApiService from '../../hooks/ApiService';
import DownloadPopper from './components/DownloadPopper';

interface IState {
  isLoaded: boolean;
}

interface IPagination {
  page: number;
  rowsPerPage: number;
  total: number;
}

const VetLabelsPage: React.FC = () => {
  const labelService = new LabelPrintTaskService();

  const labelPrintTaskService = new LabelPrintTaskService();
  const apiService = new ApiService();

  const { state } = useAuthContext();
  const { t } = useTranslation();
  const styles = sxStyles(useTheme());

  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);

  const [isConnected, setIsConnected] = React.useState<boolean>(false);
  const [farms, setFarms] = React.useState<HealthSystemDomainFarmsResultFarmViewModel[]>([]);
  const [selectedFarm, setSelectedFarm] = React.useState<HealthSystemDomainFarmsResultFarmViewModel | null>(null);
  const [vet, setVet] = React.useState<HealthSystemDomainVetsResultVeterinaryViewModel>();
  const [isPreviewDialogOpened, setIsPreviewDialogOpened] = React.useState<boolean>(false);
  const [isPrintLabelOpened, setIsPrintLabelOpened] = React.useState<boolean>(false);
  const [isPrintConfigOpened, setIsPrintConfigOpened] = React.useState(false);
  const [selectedLabel, setSelectedLabel] = React.useState<LabelPrintTask | undefined>(undefined);
  const [labelPrintCommand, setLabelPrintCommand] = React.useState<LabelPrintCommand>();
  const [printerConfig, setPrinterConfig] = React.useState<HealthSystemDALModelVetPrinterConfig>();
  const printerConfigRef = React.useRef<HealthSystemDALModelVetPrinterConfig | undefined>(undefined);

  const printLogs = React.useRef<{ status: boolean; message: string }[]>([]);
  const [consoleLogs, setConsoleLogs] = React.useState<{ text: string; dateTime: Date }[]>([]);

  const [tableState, setTableState] = React.useState<IState>({
    isLoaded: true
  });
  const [pagination, setPagination] = React.useState<IPagination>({
    page: 0,
    rowsPerPage: DEFAULT_ROWS_PER_PAGE,
    total: 0
  });
  const [labels, setLabels] = useState<LabelPrintTask[]>([]);

  const installer = usePrinterAdapterInstaller();

  // Functions__________________________________________________________________________
  React.useEffect(() => {
    printerConfigRef.current = printerConfig;
  }, [printerConfig]);

  React.useEffect(() => {
    if (!loaded) {
      loadLinkedFarms();
    }
  }, [loaded]);

  React.useEffect(() => {
    if (farms && farms.length > 0) setSelectedFarm(farms[0]);
    setTableState({ ...tableState, isLoaded: false });
  }, [farms]);

  React.useEffect(() => {
    if (!tableState.isLoaded) {
      const loadData = async () => loadLabels();
      loadData();
    }
  }, [tableState.isLoaded]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination({ ...pagination, page: newPage });
    setTableState({ ...tableState, isLoaded: false });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination({
      ...pagination,
      rowsPerPage: parseInt(event.target.value, 10)
    });
    setTableState({ ...tableState, isLoaded: false });
  };

  const loadLinkedFarms = async () => {
    // /vet/labels is behind the VIEW_VET_LABELS permission, so in practice the token is a
    // veterinary's and carries vetId. It is still only a claim: bail out with an empty farm
    // list rather than request /vets/undefined if it is ever missing.
    if (!state.vetId) {
      setFarms([]);
      setLoaded(true);
      return;
    }

    setIsBusy(true);

    await apiService.api.veterinary
      .veterinaryGetVetById({ id: state.vetId })
      .then((result) => {
        setVet(result.data);
        setPrinterConfig(result.data.settings.printerConfig);
        setFarms(result.data.farms);
        setLoaded(true);
      })
      .catch((e) => {
        setFarms([]);
        notifications.error(e?.error?.message ?? e.toString());
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleFarmChange = async (farm: any) => {
    setSelectedFarm(farm);
    setTableState({ ...tableState, isLoaded: false });
  };

  const loadLabels = async () => {
    if (selectedFarm) {
      setTableState({ ...tableState, isLoaded: false });

      const searchPage = pagination.page === 0 ? 1 : pagination.page + 1;

      labelService
        .getVetLabelPrintTasks(selectedFarm?.id as string, searchPage, pagination.rowsPerPage)
        .then((result) => {
          setLabels(result.data);
          setPagination({ ...pagination, total: result.total });
          setTableState({ ...tableState, isLoaded: true });
        })
        .catch((e) => {
          setLabels([]);
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        })
        .finally(() => setTableState({ ...tableState, isLoaded: true }));
    } else {
      setLabels([]);
      setTableState({ ...tableState, isLoaded: true });
    }
  };

  const renderStatusCell = (task: LabelPrintTask) => {
    const chipSx =
      task.status === LabelPrintTaskStatus.Error
        ? styles.redChip
        : task.status === LabelPrintTaskStatus.Success
          ? styles.greenChip
          : styles.blueChip;

    return <Chip size="small" variant="outlined" label={LabelPrintTaskStatus[task.status]} sx={chipSx} />;
  };

  const previewDialog = (labelTask: LabelPrintTask) => {
    setSelectedLabel(labelTask);
    setLabelPrintCommand(buildLabelPrintCommand(labelTask));
    setIsPreviewDialogOpened(true);
  };

  const renderLabelRow = (task: LabelPrintTask) => {
    const isCompleted = task.printedCount >= task.requestedCount;

    return (
      <TableRow key={task.id} hover>
        <TableCell>{renderStatusCell(task)}</TableCell>
        <TableCell>{new Date(task.created).toLocaleString()}</TableCell>
        <TableCell>{task.supply.number}</TableCell>
        <TableCell>{task.supply.size}</TableCell>
        <TableCell>{t(AnimalType[task.supply.animalType])}</TableCell>
        <TableCell>{`#${task.order.orderNumber}`}</TableCell>
        <TableCell>{`${task.printedCount} / ${task.requestedCount}`}</TableCell>
        <TableCell>
          <Box sx={{ flexDirection: 'row' }}>
            <IconButton
              color="primary"
              onClick={() => previewDialog(task)}
              disabled={isCompleted || !isConnected}
              sx={V4_ICON_BUTTON}
            >
              <Icon>print</Icon>
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const buildHubConnection = () => {
    return new HubConnectionBuilder().withUrl(PRINTER_ADAPTER_HUB_URL).withAutomaticReconnect().build();
  };

  const [connection] = React.useState<HubConnection>(() => buildHubConnection());

  const startConnection = () => {
    connection
      .start()
      .then(() => {
        setIsConnected(true);
        setConsoleLogs((prev) => [{ text: `Notifications Hub connected`, dateTime: new Date() }, ...prev]);
      })
      .catch(async (ex) => {
        setIsConnected(false);
        setConsoleLogs((prev) => [
          { text: `Notifications Hub connection failed: ${ex.toString()}`, dateTime: new Date() },
          ...prev
        ]);

        const reason = await probeLocalNetwork(PRINTER_ADAPTER_ORIGIN);

        switch (reason) {
          case 'permission-denied':
            notifications.warning(t('f-conn-print-adapter-denied'));
            break;
          case 'insecure-context':
            notifications.warning(t('f-conn-print-adapter-insecure'));
            break;
          case 'unreachable':
            notifications.warning(t('f-conn-print-adapter-unreachable'));
            break;
          default:
            notifications.warning(t('f-conn-print-adapter-failed'));
        }
      });
  };

  const stopConnection = () => {
    connection
      .stop()
      .then(() => {
        setIsConnected(false);
      })
      .catch((ex) => {
        setConsoleLogs((prev) => [
          { text: `Notifications Hub stop connection failed: ${ex.toString()}`, dateTime: new Date() },
          ...prev
        ]);
      });
  };

  const buildLabelPrintCommand = (labelTask: LabelPrintTask): LabelPrintCommand => {
    // milk for cows/goat, meat for rest
    const withdrawalFor =
      labelTask.supply.animalType === AnimalType.Cow || labelTask.supply.animalType === AnimalType.Goat
        ? 'milk'
        : 'meat';
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0'); //January is 0!
    const yyyy = d.getFullYear();
    const date = `${dd}/${mm}/${yyyy}`;

    return {
      id: labelTask.id,
      medicalSupply: labelTask.supply.number,
      animalType: AnimalType[labelTask.supply.animalType],
      size: labelTask.supply.size,
      dose: labelTask.supply.dose,
      orderNumber: labelTask.order.orderNumber,
      lotNumber: labelTask.supply.overriddenLotNumber ?? labelTask.supply.lotNumber ?? '',
      treatNumber: labelTask.supply.overriddenTreatmentNumber ?? labelTask.supply.treatmentNumber ?? '',
      withdrawal: labelTask.supply.withdrawal,
      withdrawalFor: withdrawalFor,
      method: t(medicalSupplyMethodKeys[labelTask.supply.method]),
      farmNumber: labelTask.farm.number,
      farmName: labelTask.farm.name,
      farmAddress: labelTask.farm.address,
      vetAddress: vet?.settings.address,
      vetName: vet?.name,
      vetContactName: state.userName,
      vetPhone: vet?.settings?.phone,
      date: date,
      regNumber: '',
      daysToUse: labelTask.supply.daysToUse?.toString(),
      minVolumeCC: labelTask.supply.minVolumeCC,
      maxVolumeCC: labelTask.supply.maxVolumeCC,
      startVolumeCC: labelTask.supply.startVolume,
      incrementStep: labelTask.supply.incrementStep
    } as LabelPrintCommand;
  };

  // Sends request for printing one label and opens printing dialog
  const printLabel = (command: PrintCommand) => {
    setLabelPrintCommand(command.payload);
    sendCommandToAdapter(command);
    setIsPrintLabelOpened(true);
  };

  const getPrinterConfig = () => {
    return printerConfigRef.current;
  };

  const handlePrinterConfigSaved = (config: HealthSystemDALModelVetPrinterConfig) => {
    setPrinterConfig(config);
    if (vet) {
      setVet({ ...vet, settings: { ...vet.settings, printerConfig: config } });
    }
  };

  const sendCommandToAdapter = (command: PrintCommand) => {
    connection.invoke('PrintCommand', JSON.stringify(command)).catch((e) => {
      notifications.error(`${e}`);
    });
  };

  const onConfirm = () => {
    if (selectedLabel && selectedLabel.printedCount < selectedLabel.requestedCount) {
      const printedCount = (selectedLabel?.printedCount ?? 0) + 1;

      setSelectedLabel({ ...selectedLabel, printedCount });
      printLogs.current.push({
        status: true,
        message: `User confirmed label printed, ${printedCount}/${selectedLabel?.requestedCount}`
      });
      setConsoleLogs([
        {
          text: `User confirmed label printed, ${printedCount}/${selectedLabel?.requestedCount}`,
          dateTime: new Date()
        },
        ...consoleLogs
      ]);

      // No sense to print if it's X of X requested labels
      if (selectedLabel.printedCount < selectedLabel.requestedCount - 1) {
        sendCommandToAdapter({ payload: labelPrintCommand, config: getPrinterConfig() } as PrintCommand);
      }
    }
  };

  const onReject = () => {
    printLogs.current.push({
      status: false,
      message: `User rejected label printed, ${(selectedLabel?.printedCount ?? 0) + 1}/${selectedLabel?.requestedCount}`
    });
    setConsoleLogs([
      {
        text: `User rejected label printed, ${(selectedLabel?.printedCount ?? 0) + 1}/${selectedLabel?.requestedCount}`,
        dateTime: new Date()
      },
      ...consoleLogs
    ]);
    onPrintDialogClose();
    setIsPrintLabelOpened(false);
  };

  const onPrintDialogClose = () => {
    if (printLogs.current.filter((p) => p.status).length > 0) {
      let message = '';
      printLogs.current.forEach((p) => (message = message + `${p.message}\n`));

      if (selectedLabel)
        labelPrintTaskService
          .updateLabelPrintTask(
            selectedLabel?.id,
            selectedLabel.printedCount < selectedLabel.requestedCount
              ? LabelPrintTaskStatus.Error
              : LabelPrintTaskStatus.Success,
            selectedLabel?.printedCount,
            printLogs.current.map((pl) => pl.message),
            labelPrintCommand?.lotNumber || undefined,
            labelPrintCommand?.treatNumber || undefined
          )
          .then(() => {
            loadLabels().then();
          })
          .catch((e) => {
            notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
          });
    }
    printLogs.current = [];
  };

  React.useEffect(() => {
    if (connection) {
      startConnection();

      const handler = (result: LogLineResult[]) => {
        result.forEach((item) => {
          let message = t(item.messageId, toI18nParams(item.parameters));

          if (message === undefined) {
            message = item.messageId;
          }

          if (item.isSuccess) {
            notifications.success(message);
          } else {
            notifications.warning(message);
          }
        });
      };

      connection.on('print-command', handler);

      connection.onclose(() => {
        setIsConnected(false);
        notifications.info(t('f-hubs-notif-discon'));
      });

      connection.onreconnected(() => {
        setIsConnected(true);
        notifications.info(t('f-hubs-notif-con'));
      });
    }
  }, [connection]);

  return (
    <>
      <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
        <Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex' }}>
              <Grid container spacing={1}>
                <Icon sx={styles.headerIcon} fontSize={'large'}>
                  sticky_note_2
                </Icon>
                <Box sx={{ ml: 1, mt: 0.5 }}>
                  <Title>{t('f-labels')}</Title>
                </Box>
                <Grid item>
                  <Chip label={pagination.total} variant="outlined" />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ alignItems: 'right' }}>
              <ConnectionStatePopper
                start={startConnection}
                stop={stopConnection}
                isConnected={isConnected}
                logs={consoleLogs}
              />

              <IconButton onClick={() => setIsPrintConfigOpened(true)} size="small" sx={V4_ICON_BUTTON_SMALL}>
                <Icon fontSize="large" color="primary" style={{ color: !isConnected ? red.A400 : undefined }}>
                  {'settings'}
                </Icon>
              </IconButton>

              <DownloadPopper
                isConnected={isConnected}
                installerUrl={installer.release?.url ?? ''}
                installerVersion={installer.release?.version}
                installerUnavailable={installer.failed}
              />
            </Box>
          </Box>

          <Grid container>
            <Grid item container alignItems={'center'}>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  id="combo-box-demo"
                  options={farms}
                  size="small"
                  style={{ marginLeft: 0 }}
                  sx={styles.formControl}
                  getOptionLabel={(option) => option.name ?? ''}
                  disabled={!farms}
                  value={selectedFarm}
                  renderInput={(params) => (
                    <AutocompleteInput params={params} label={t('pls-sel-farm')} variant="outlined" />
                  )}
                  onChange={(_, newValue) => handleFarmChange(newValue)}
                />
              </Grid>
            </Grid>

            <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

            <TablePaginationWrapper
              rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
              count={pagination.total}
              rowsPerPage={pagination.rowsPerPage}
              page={pagination.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            >
              <TableContainer component={Paper} elevation={1} sx={styles.mt2}>
                <Table>
                  <TableBody>
                    {tableState.isLoaded ? (
                      labels && labels.length > 0 ? (
                        <TableRow>
                          <TableCell>{t('f-com-status')}</TableCell>
                          <TableCell>{t('f-com-date-time')}</TableCell>
                          <TableCell>{t('f-com-supply')}</TableCell>
                          <TableCell>{t('f-com-amount')}</TableCell>
                          <TableCell>{t('f-com-animal-type')}</TableCell>
                          <TableCell>{t('f-supp-order-number')}</TableCell>
                          <TableCell>{t('f-progress')}</TableCell>
                          <TableCell />
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell variant="head" sx={styles.noDataBox}>
                            {t('f-no-data')}
                          </TableCell>
                        </TableRow>
                      )
                    ) : (
                      <TableRow sx={styles.spinnerBox}>
                        <TableCell>
                          <Loader size={LoaderSize.MEDIUM} isLoading />
                        </TableCell>
                      </TableRow>
                    )}

                    {labels && labels.length > 0 && tableState.isLoaded ? labels.map((l) => renderLabelRow(l)) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </TablePaginationWrapper>
          </Grid>
        </Grid>
      </Container>

      <LabelPreviewDialog
        opened={isPreviewDialogOpened}
        setOpened={setIsPreviewDialogOpened}
        callback={() => printLabel({ payload: labelPrintCommand, config: getPrinterConfig() } as PrintCommand)}
        labelPrintCommand={labelPrintCommand}
        setLabelPrintCommand={setLabelPrintCommand}
      />

      <PrintLabelsDialog
        opened={isPrintLabelOpened}
        setDialog={setIsPrintLabelOpened}
        labelPrintCommand={labelPrintCommand}
        setSelLabel={setSelectedLabel}
        onConfirm={onConfirm}
        onClose={onPrintDialogClose}
        onReject={onReject}
        labelTask={labelPrintCommand}
        selLabel={selectedLabel}
      />

      <PrintCommandConfigModal
        opened={isPrintConfigOpened}
        setDialogOpen={setIsPrintConfigOpened}
        isConnected={isConnected}
        connection={connection}
        start={startConnection}
        stop={stopConnection}
        veterinary={vet}
        onConfigSaved={handlePrinterConfigSaved}
      />
    </>
  );
};

export default VetLabelsPage;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    headerIcon: {
      color: theme.palette.primary.main
    },
    spinnerBox: {
      marginTop: '32px',
      marginBottom: '32px'
    },
    noDataBox: {
      minHeight: 75,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'gray'
    },
    formControl: {
      margin: theme.spacing(1)
    },
    header: {
      paddingTop: '4px'
    },
    autoAlign: {
      alignItems: 'center',
      padding: theme.spacing(1),
      paddingLeft: 0,
      paddingRight: 0,
      [theme.breakpoints.up('sm')]: {
        justifyContent: 'flex-end'
      }
    },
    mt2: {
      marginTop: theme.spacing(2)
    },
    redChip: {
      color: colors.red,
      borderColor: colors.red
    },
    greenChip: {
      color: colors.green,
      borderColor: colors.green
    },
    blueChip: {
      color: colors.blue,
      borderColor: colors.blue
    }
  }) as const;
